// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';
import { AskSession, PatchAskSessionRequest } from '../types';

// ============= Constants =============
const ASK_BASE_URL = import.meta.env.VITE_BASE_ASK_URL;
const ASK_API_ENDPOINT = '/api/AskSession';
const ASK_LITIUM_SCOPE =
  'api://c98b7623-f120-463e-9a81-42e287c0ab58/access_as_user';

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface PatchAskSessionProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  askSessionId: string;
  data: PatchAskSessionRequest;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Edit an AskSession
 * @param getAccessToken Function to acquire access token
 * @param askSessionId Ask Session identifier
 * @param data Request body for updating Ask Session
 * @returns Promise with API response containing updated Ask Session data or error
 */
export const patchAskSession = async ({
  getAccessToken,
  askSessionId,
  data,
}: PatchAskSessionProps): Promise<AskSession> => {
  try {
    console.log('🔄 Updating Ask Session...');

    // Token acquisition with required scopes
    const token = await getAccessToken([ASK_LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${ASK_BASE_URL}${ASK_API_ENDPOINT}/${askSessionId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'PATCH',
      headers: createHeaders(token),
      body: JSON.stringify(data),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Ask Session updated successfully');

    return result;
  } catch (error) {
    console.error('🔴 Error updating Ask Session:', error);
    throw error;
  }
};
